import React from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import {
  Code,
  TrendingUp,
  Campaign,
  AccountBalance,
  People,
  Settings,
  Add,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showSnackbar } from "../../store/slices/uiSlice";

const DEPT_META: Record<
  string,
  { color: string; bg: string; icon: React.ReactNode }
> = {
  Engineering: {
    color: "#85D1DB",
    bg: "rgba(133,209,219,0.08)",
    icon: <Code />,
  },
  Sales: {
    color: "#B6F2D1",
    bg: "rgba(182,242,209,0.08)",
    icon: <TrendingUp />,
  },
  Marketing: {
    color: "#6BB8C3",
    bg: "rgba(107,184,195,0.08)",
    icon: <Campaign />,
  },
  Finance: {
    color: "#22c55e",
    bg: "rgba(34,197,94,0.08)",
    icon: <AccountBalance />,
  },
  HR: { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", icon: <People /> },
  Operations: {
    color: "#f43f5e",
    bg: "rgba(244,63,94,0.08)",
    icon: <Settings />,
  },
};

// Using global theme for table headers

const Departments: React.FC = () => {
  const dispatch = useAppDispatch();
  const departments = useAppSelector((s) => s.data.departments);
  const totalEmp = departments.reduce((a, d) => a + d.employees, 0);
  const totalOpen = departments.reduce((a, d) => a + d.openRoles, 0);

  return (
    <Box>
      <SEO title="Departments" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          { icon: "🏢", val: departments.length, lbl: "Departments" },
          { icon: "👥", val: totalEmp, lbl: "Total Employees" },
          { icon: "💼", val: totalOpen, lbl: "Open Roles" },
        ].map((s) => (
          <Grid item xs={12} sm={4} key={s.lbl}>
            <Card sx={{ textAlign: "center", p: 0 }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 28 }}>{s.icon}</Typography>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                  }}
                >
                  {s.val}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                  {s.lbl}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {departments.map((d) => {
          const meta = DEPT_META[d.name] || DEPT_META.Engineering;
          return (
            <Grid item xs={12} sm={6} lg={4} key={d.id}>
              <Card
                sx={{
                  p: 0,
                  borderTop: `3px solid ${meta.color} !important`,
                  transition: "box-shadow 0.2s, transform 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08) !important",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: "18px !important",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        background: meta.bg,
                        color: meta.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {meta.icon}
                    </Box>
                    {d.openRoles > 0 && (
                      <Chip
                        label={`${d.openRoles} open`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: "99px",
                          background: meta.bg,
                          color: meta.color,
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {d.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    👤 {d.head}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        p: "10px",
                        textAlign: "center",
                        borderRight: "1px solid #e5e7eb",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          fontFamily: "'JetBrains Mono',monospace",
                        }}
                      >
                        {d.employees}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        Employees
                      </Typography>
                    </Box>
                    <Box sx={{ p: "10px", textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          fontFamily: "'JetBrains Mono',monospace",
                        }}
                      >
                        ₹{(d.avgSalary / 1000).toFixed(0)}K
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        Avg Salary
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        Budget Used
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: d.budgetUsed > 80 ? "#dc2626" : meta.color,
                        }}
                      >
                        {d.budgetUsed}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={d.budgetUsed}
                      sx={{
                        height: 6,
                        borderRadius: 99,
                        backgroundColor: "#e2e8f0",
                        "& .MuiLinearProgress-bar": {
                          background: meta.color,
                          borderRadius: 99,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            Department Details
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={() =>
              dispatch(showSnackbar({ message: "New department created" }))
            }
            sx={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 99,
              backgroundColor: "#ccfbf1",
              color: "#0f766e",
              boxShadow: "none",
              px: 2,
              "&:hover": {
                backgroundColor: "#99f6e4",
                boxShadow: "none",
              },
            }}
          >
            Add Dept
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "DEPARTMENT",
                  "HEAD",
                  "EMPLOYEES",
                  "AVG SALARY",
                  "BUDGET USED",
                  "OPEN ROLES",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748b",
                      letterSpacing: "0.05em",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((d) => {
                const meta = DEPT_META[d.name] || DEPT_META.Engineering;
                return (
                  <TableRow
                    key={d.id}
                    sx={{
                      transition: "background 0.15s",
                      "&:hover": { background: "#fafbff" },
                    }}
                  >
                    <TableCell
                      sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: meta.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {d.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        fontSize: 13,
                        color: "#64748b",
                      }}
                    >
                      {d.head}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        fontSize: 13,
                        fontFamily: "'JetBrains Mono',monospace",
                        fontWeight: 600,
                      }}
                    >
                      {d.employees}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        fontSize: 13,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      ₹{d.avgSalary.toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell
                      sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={d.budgetUsed}
                          sx={{
                            width: 80,
                            height: 6,
                            borderRadius: 99,
                            backgroundColor: "#e2e8f0",
                            "& .MuiLinearProgress-bar": {
                              background: "#85D1DB",
                              borderRadius: 99,
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontFamily: "'JetBrains Mono',monospace",
                            color: "#64748b",
                          }}
                        >
                          {d.budgetUsed}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          background: d.openRoles > 0 ? meta.bg : "#f1f5f9",
                          color: d.openRoles > 0 ? meta.color : "#94a3b8",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {d.openRoles}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default Departments;
